import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  SimpleGrid,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Skeleton,
  useToast,
  Icon,
  useDisclosure,
} from '@chakra-ui/react'
import { keepPreviousData, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageXIcon, PlusIcon, Trash04Icon } from '../../assets/icons'
import ContentTypePickerModal from '../modals/ContentTypePickerModal'
import formatDate from '../../helpers/formatDate'
import handleToast from '../HandleToast'
import FilterButton from './base/FilterButton'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import ContentCard from '../cards/ContentCard'
import { fetchContentList, deleteContent, fetchPublishedContentList, fetchTopics } from '../../api/content/content'
import { fetchEmployeeList } from '../../api/workforce/workforce'
import { ContentStatus, ContentType } from '../../types/content/content'
import { useRole } from '../../hooks/auth/useRole'
import DiscardModal from '../modals/discardModal'
import { useNavigate } from 'react-router-dom'
import 'react-lazy-load-image-component/src/effects/opacity.css'
import LazyLoadImg from '../image/LazyLoadImg'
import typeMapping from '../../themes/contentTypeMapping'
import statusMapping from '../../themes/contentStatusMapping'
import ExploreContentModal from '../modals/ExploreContentModal'
import ViewSwitch from '../input/DocumentContentSwitch'
import ListGridSwitcher from '../input/ListGridSwich'
import { prefetchContentRowIntent } from '../../lib/performance/prefetch'

interface ContentTableProps {
  explore?: boolean;
  assigned_to?: string;
  viewing?: 'content' | 'documents';
  setViewing?: (view: 'content' | 'documents') => void;
  showCheckbox?: boolean;
}

const ContentTable: React.FC<ContentTableProps> = ({ explore = false, assigned_to, viewing, setViewing, showCheckbox = true }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const itemsPerPage = 10
  const toast = useToast()
  const [duration, setDuration] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const [searchTerm, setSearchTerm] = useState('')
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('list')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [selectedContentItem, setSelectedContentItem] = useState<any>(null)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [isCreateOptionsOpen, setIsCreateOptionsOpen] = useState(false)

  const { isAdmin, isUserMode } = useRole()

  // --- Filter state ---
  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  // --- Static filter data: content_type ---
  const contentTypeOptions = useMemo(() => [
    { id: 'skills', name: t('content.types.skills') },
    { id: 'competence', name: t('content.types.competence') },
    { id: 'task', name: t('content.types.task') },
    { id: 'goal', name: t('content.types.goal') },
    { id: 'note', name: t('content.types.note') },
    { id: 'learning', name: t('content.types.learning') },
    { id: 'training', name: t('todo.trainAndTest') },
    { id: 'survey', name: t('content.types.survey') },
    { id: 'anonymous_survey', name: t('content.types.anonymous_survey') },
    { id: 'form', name: t('content.types.form') },
  ], [t])

  // --- Static filter data: contentStatus ---
  const contentStatusOptions = useMemo(() => [
    { id: 'draft', name: t('common.statuses.draft') },
    { id: 'published', name: t('common.statuses.published') },
    { id: 'unpublished', name: t('common.statuses.unpublished') },
    { id: 'anonymized', name: t('common.statuses.anonymized') },
  ], [t])

  // --- Infinite scroll filter data: Author (maps to created_by) ---
  const [authorFilterSearch, setAuthorFilterSearch] = useState('')
  const authorFilterQuery = useInfiniteQuery({
    queryKey: ['filter-authors', 10, authorFilterSearch],
    queryFn: ({ pageParam }) =>
      fetchEmployeeList({ search: authorFilterSearch, page: pageParam, pageSize: 10, filters: {}, minimal: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.has_next ? allPages.length + 1 : undefined,
  })
  const authorFilterList = useMemo(
    () =>
      (authorFilterQuery.data?.pages ?? []).flatMap((p: any) =>
        Array.isArray(p?.data)
          ? p.data.map((a: any) => ({ id: a.id, name: `${a.first_name} ${a.last_name}` }))
          : [],
      ),
    [authorFilterQuery.data?.pages],
  )

  // --- Static filter data: Topic (fetched from API, no pagination) ---
  const topicsQuery = useQuery({
    queryKey: ['filter-topics'],
    queryFn: () => fetchTopics(),
    enabled: !explore,
  })
  const topicFilterList = useMemo(() =>
    Array.isArray(topicsQuery.data)
      ? topicsQuery.data.map((topic: string) => ({ id: topic, name: topic }))
      : [],
    [topicsQuery.data]
  )

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const content = !explore
    ? useQuery({
      queryKey: ['content', page, searchTerm, filtersKey, assigned_to],
      // keep recently prefetched pages fresh for a short time to avoid immediate refetch
      staleTime: 60 * 1000, // 1 minute
      placeholderData: keepPreviousData,
      queryFn: () =>
        fetchContentList({
          page,
          pageSize: itemsPerPage,
          search: searchTerm,
          filters,
          assigned_to: assigned_to ?? undefined,
        }),
    })
    : useQuery({
      queryKey: ['content', page, searchTerm, filtersKey],
      // keep recently prefetched pages fresh for a short time to avoid immediate refetch
      staleTime: 60 * 1000, // 1 minute
      placeholderData: keepPreviousData,
      queryFn: () =>
        fetchPublishedContentList({
          page,
          pageSize: itemsPerPage,
          search: searchTerm,
          filters,
          status: 'published',
        }),
    })

  // Prefetch next page when current page looks full (likely more data)
  React.useEffect(() => {
    const currentCount = content.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    if (!explore) {
      queryClient.prefetchQuery({
        queryKey: ['content', nextPage, searchTerm, filtersKey, assigned_to],
        // keep prefetched data fresh for the same duration so it won't be refetched on mount
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchContentList({
            page: nextPage,
            pageSize: itemsPerPage,
            search: searchTerm,
            filters,
            assigned_to: assigned_to ?? undefined,
          }),
      })
    } else {
      queryClient.prefetchQuery({
        queryKey: ['content', nextPage, searchTerm, filtersKey],
        // keep prefetched data fresh for the same duration so it won't be refetched on mount
        staleTime: 60 * 1000,
        queryFn: () =>
          fetchPublishedContentList({
            page: nextPage,
            pageSize: itemsPerPage,
            search: searchTerm,
            filters,
            status: 'published',
          }),
      })
    }
  }, [page, searchTerm, filters, filtersKey, assigned_to, explore, content.data?.data?.length, queryClient])

  const handleRowClick = (row: RowData) => {
    if (explore) {
      setSelectedContentItem(row)
      onOpen()
    } else {
      navigate(`/user/content/${row.id}`)
    }
  }

  const handleConfirmDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => deleteContent(id)))
      queryClient.invalidateQueries({ queryKey: ['content'] })
      handleToast(toast, true, t('common.states.success'), t('messages.deleteSuccess'))
    } catch (error) {
      handleToast(toast, false, t('common.states.error'), t('messages.deleteFailed'))
    }
    setSelectedRows([])
    setShowDiscardModal(false)
  }

  const handleDeleteContents = () => {
    setShowDiscardModal(true)
  }

  const contentData = content?.data?.data || []
  const totalItems = content.data?.count ?? 0
  const computedMaxPage = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const apiMaxPage = typeof content.data?.max_page === 'number' && content.data.max_page > 0 ? content.data.max_page : undefined
  const effectiveMaxPage = apiMaxPage ? Math.min(apiMaxPage, computedMaxPage) : computedMaxPage

  const handleSearch = (searchTerm: string) => {
    setPage(1)
    setSearchTerm(searchTerm)
  }

  useEffect(() => {
    setPage(1)
  }, [searchTerm, filtersKey])

  useEffect(() => {
    if (page > effectiveMaxPage) {
      setPage(1)
    }
  }, [page, effectiveMaxPage])

  const handleTypeSelect = (type: ContentType) => {
    navigate(`/user/content/new?type=${type}`)
  }

  const columns: Column[] = [
    ...(isAdmin && showCheckbox && !explore ? [{ label: '', accessor: 'select', type: 'select' as const, width: '5%' }] : []),
    {
      label: t('content.fields.content'),
      accessor: 'content',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => (
        <Flex alignItems="center" gap="12px">
          <Box
            bg="gray.100"
            minW="102px"
            maxW="102px"
            borderRadius="8px"
            width={'100%'}
            display={'flex'}
            height="64px"
            justifyContent="center"
            alignItems="center"
            overflow="hidden"
          >
            {!row.thumbnail ? (
              <Flex
                width="100%"
                height="100%"
                alignItems="center"
                justifyContent="center"
                bg="solid.25"
                flexDirection="column"
              >
                <Icon boxSize="20px" color="solid.100">
                  <ImageXIcon />
                </Icon>
              </Flex>
            ) : !row.thumbnail.includes('.mp4') ? (
              <LazyLoadImg
                src={row.thumbnail}
                borderRadius="8px"
                w="100%"
                h="100%"
                objectFit="cover"
              />
            ) : (
              <Box
                position="relative"
                width="100%"
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                bg="gray.800"
                borderRadius="md"
                overflow="hidden"
              >
                <video
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      const minutes = Math.floor(videoRef.current.duration / 60)
                      const seconds = Math.floor(videoRef.current.duration % 60)
                      const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`
                      if (!isNaN(minutes) && !isNaN(seconds)) {
                        setDuration(formattedDuration)
                      }
                    }
                  }}
                >
                  <source src={row.thumbnail + '#t=0.1'} />
                </video>
                {duration && (
                  <Text
                    position="absolute"
                    bottom="5px"
                    right="5px"
                    bg="rgba(0, 0, 0, 0.7)"
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                    px="6px"
                    py="2px"
                    borderRadius="4px"
                  >
                    {duration}
                  </Text>
                )}
              </Box>
            )}
          </Box>
          <Flex direction="column">
            <Text textStyle="h-xs">{row.title}</Text>
            <Text color="solid.500" textStyle="b-xs" noOfLines={1} mt="4px" lineHeight="normal">
              {row.description}
            </Text>
          </Flex>
        </Flex>
      ),
      width: '400px',
    },
    {
      label: t('common.fields.topic'),
      accessor: 'topic',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        return (
          <Text textStyle="l-xs">
            {row.topic}
          </Text>
        )
      },
    },
    {
      label: t('common.fields.author'),
      accessor: 'created_by',
      type: 'custom',
      
      Cell: ({ row }: { row: RowData }) => {
        
        return (
          <Text textStyle="l-xs" noOfLines={1}>
            {row.created_by}
          </Text>
        )
      },
    },
    {
      label: t('common.fields.publishedDate'),
      accessor: 'published_date',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        return <Text textStyle="b-xs">{formatDate(row.published_at)}</Text>
      },
    },
    ...(explore
      ? []
      : [
        {
          label: t('common.fields.status'),
          accessor: 'status',
          type: 'custom' as const,
          Cell: ({ row }: { row: RowData }) => {
            const status = statusMapping[row.status as keyof typeof statusMapping]

            const statusKey = `common.statuses.${row.status}`
            const translatedStatus = t(statusKey)
            const statusLabel = translatedStatus !== statusKey ? translatedStatus : status.label

            return (
              <Tag
                borderRadius="14px"
                h="28px"
                bg={status.bg}
                color={status.color}
                borderWidth="1px"
                borderColor={status.borderColor}
              >
                <TagLeftIcon>{status.icon}</TagLeftIcon>
                <TagLabel textStyle="h-xxs">{statusLabel}</TagLabel>
              </Tag>
            )
          },
        },
      ]),
    {
      label: t('content.fields.contentType'),
      accessor: 'content_type',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const typeInfo = typeMapping[row.content_type as keyof typeof typeMapping]

        if (!typeInfo) return null

        const typeKey = row.content_type === 'training'
          ? 'todo.trainAndTest'
          : `content.types.${row.content_type}`
        const translatedType = t(typeKey)
        const typeLabel = translatedType !== typeKey ? translatedType : typeInfo.label

        return (
          <Tag
            borderRadius="14px"
            h="28px"
            bg={typeInfo?.bg}
            color={typeInfo?.color}
            borderWidth="1px"
            borderColor={typeInfo?.borderColor}
          >
            <TagLeftIcon>{typeInfo?.icon}</TagLeftIcon>
            <TagLabel textStyle="h-xxs" pb={'1px'}>
              {typeLabel}
            </TagLabel>
          </Tag>
        )
      },
    },
  ]

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const normalizedId = String(id)
    if (isChecked) {
      setSelectedRows((prev) => (prev.includes(normalizedId) ? prev : [...prev, normalizedId]))
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== normalizedId))
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prev) => {
        const newIds = contentData.map((row: { id: string | number }) => String(row.id))
        return Array.from(new Set([...prev, ...newIds]))
      })
    } else {
      setSelectedRows((prev) =>
        prev.filter((id) => !contentData.some((row: { id: string | number }) => String(row.id) === id)),
      )
    }
  }

  const currentPageRowIds = contentData.map((row: { id: string | number }) => String(row.id))
  const allSelected = contentData.length > 0 && currentPageRowIds.every((id) => selectedRows.includes(id))
  const isIndeterminate =
    contentData.length > 0 && currentPageRowIds.some((id) => selectedRows.includes(id)) && !allSelected

  return (
    <>
      <Card width="100%" p="0">
        <Grid>
          <Box>
            <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
              <SearchInput onSearch={handleSearch} value={searchTerm} />

              <Flex gap="8px">
                {selectedRows.length > 0 && isAdmin ? (
                  <Button 
                    onClick={handleDeleteContents} 
                    variant="error"
                    size="sm"
                    h="32px"
                    px="14px"
                    fontSize="13px"
                    fontWeight="600"
                    borderRadius="8px"
                  >
                    <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" mr={2}>
                      <Trash04Icon />
                    </Icon>
                    {t('common.modals.actionCountTitle', {
                      action: t('common.actions.delete'),
                      count: selectedRows.length,
                      item: t('common.labels.row', { count: selectedRows.length }),
                    })}
                  </Button>
                ) : (
                  <>
                    <FilterButton
                      label={t('common.fields.types')}
                      options={contentTypeOptions}
                      onApply={(selected) => updateFilter('content_type', selected)}
                    />
                    {!explore && (
                      <FilterButton
                        label={t('common.fields.status')}
                        options={contentStatusOptions}
                        onApply={(selected) => updateFilter('status', selected)}
                      />
                    )}
                    <FilterButton
                      label={t('common.fields.author')}
                      options={authorFilterList}
                      onApply={(selected) => updateFilter('created_by', selected)}
                      search={authorFilterSearch}
                      setSearch={setAuthorFilterSearch}
                      onLoadMore={() => authorFilterQuery.fetchNextPage()}
                      hasMore={!!authorFilterQuery.hasNextPage}
                      isLoadingMore={authorFilterQuery.isFetchingNextPage}
                      isLoading={authorFilterQuery.isFetching && !authorFilterQuery.isFetchingNextPage}
                    />
                    {!explore && (
                      <FilterButton
                        label={t('common.fields.topic')}
                        options={topicFilterList}
                        onApply={(selected) => updateFilter('topic', selected)}
                      />
                    )}

                    {viewing !== undefined && setViewing !== undefined && (
                      <ViewSwitch viewing={viewing} setViewing={setViewing} />
                    )}

                    <ListGridSwitcher
                      isGrid={selectedView === 'grid'}
                      onToggle={() => setSelectedView(selectedView === 'grid' ? 'list' : 'grid')}
                    />

                    {isAdmin && !isUserMode && !explore && !assigned_to &&(
                      <Flex gap={3}>
                        <Button 
                          variant="mono" 
                          onClick={() => setIsCreateOptionsOpen(true)}
                          size="sm"
                          h="32px"
                          px="14px"
                          fontSize="13px"
                          fontWeight="600"
                          borderRadius="8px"
                        >
                          <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                            <PlusIcon />
                          </Icon>
                          {t('common.actions.create')}
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>

            <Box minHeight="58vh">
              {selectedView === 'grid' && content.isLoading && (
                <SimpleGrid minChildWidth="260px" gap="16px" p="12px 20px">
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                  <Skeleton h="243px" borderRadius="12px" />
                </SimpleGrid>
              )}
              {selectedView === 'grid' && !content.isLoading && (
                <SimpleGrid minChildWidth="260px" gap="16px" p="12px 20px">
                  {(
                    contentData as Array<{
                      id: string
                      title: string
                      description: string
                      thumbnail: string
                      status: string
                      published_at: string
                      views: number
                      content_type: ContentType
                    }>
                  ).map((contentItem) => (
                    <ContentCard
                      key={contentItem.id}
                      type={contentItem.content_type as ContentType}
                      title={contentItem.title}
                      media={contentItem.thumbnail}
                      status={contentItem.status as ContentStatus}
                      timestamp={contentItem.published_at}
                      views={contentItem.views}
                      description={contentItem.description}
                      onSelect={() => handleRowClick(contentItem)}
                    />
                  ))}
                </SimpleGrid>
              )}
              {selectedView === 'list' &&
                (content.isLoading ? (
                  <LoadingTable columns={columns} />
                ) : (
                  <MasterTable
                    columns={columns}
                    data={contentData || []}
                    onRowClick={handleRowClick}
                    onRowIntent={(row) => !explore && prefetchContentRowIntent(queryClient, row.id)}
                    selectedRows={selectedRows}
                    onSelectAll={handleSelectAll}
                    onSelectRow={handleSelectRow}
                    allSelected={allSelected}
                    isIndeterminate={isIndeterminate}
                    tableType="content"
                  />
                ))}
            </Box>
          </Box>
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={effectiveMaxPage}
            totalItems={totalItems}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Grid>

        {isAdmin && (
          <DiscardModal
            show={showDiscardModal}
            handleClose={() => setShowDiscardModal(false)}
            handleDelete={handleConfirmDelete}
            action="delete"
            notRow={false}
            selectedRowsCount={selectedRows.length}
          />
        )}
      </Card>

      <ExploreContentModal isOpen={isOpen && explore} onClose={onClose} contentItem={selectedContentItem} />

      <ContentTypePickerModal
        isOpen={isCreateOptionsOpen}
        onClose={() => setIsCreateOptionsOpen(false)}
        onSelectType={handleTypeSelect}
      />
    </>
  )
}

export default ContentTable
